import * as Yup from 'yup';
import { AddCircleIcon, CoinPlusIcon } from '@synergy/theme-next';
import { ArrayHelpers, FieldArray, FormikProps, useFormikContext } from 'formik';
import { Box, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components-next';
import { PaloDescriptionModeToggle, PaloFormSection } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolButton, PoolSplitButton } from '@synergy/components-next';
import { errorsToTouch } from '@synergy/utils';
import { useState } from 'react';
import dayjs from 'dayjs';

import { CustomPosition } from '../../../../../common/components/free-position-dialog/model/custom-position';
import { CustomPositionFields } from '../../../../../common/components/free-position-dialog/model/custom-position-fields';
import { DemandDetailPositionsTable } from '../pages/demand-detail-positions-table';
import { DemandItem } from '../../../models/demand-item';
import { DemandSteps } from '../models/demand-steps';
import { FormikGroupItem } from '../../../../../common/components/new-stepper/formik-group-item';
import { FreePositionDialog } from '../../../../../common/components/free-position-dialog/free-position-dialog';
import { PaloAddPositionsDialog } from '../../../../../common/components/palo/palo-add-positions-dialog';
import { PositionsFormValues, fieldNames } from '../models/positions-form-values';
import { Product } from '../../../../../common/models/product';
import { bestLocaleSelector } from '../../../../../common/selectors/locale-selectors';
import {
    demandDetailPositionsBaselineSelector,
    demandDetailPositionsDataSelector,
} from '../store/positions/demand-detail-positions-selectors';
import { demandDetailStepperStateSelector } from '../store/demand-detail-selectors';
import { submitPositions } from '../store/positions/demand-detail-positions-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export type DemandDetailPositionsArrayHelpers = ArrayHelpers & {
    form: FormikProps<PositionsFormValues>;
    name: string;
};

interface CustomData {
    index?: number;
    wasNormArticle: boolean;
}

/**
 * Nothing is required while the demand is a draft: it may be prepared in several
 * steps. Positions and delivery period become necessary when it is started, and
 * the start says what is missing (GF 14.10.2026, #2253).
 */
const validationSchema = Yup.object().shape({
    [fieldNames.deliveryDateFrom]: Yup.date().nullable(),
    [fieldNames.deliveryDateTo]: Yup.date().nullable(),
});

/**
 * The delivery period in the head line of the positions: it applies to every
 * one of them and needs no section of its own (#2253).
 */
const PaloPositionsDeliveryRange = ({ readonly }: { readonly: boolean }): JSX.Element => {
    const { deliveryDateFrom, deliveryDateTo } = useFormikContext<PositionsFormValues>().values;

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 450, fontSize: '11.5px' }}
        >
            <FormattedMessage id="app.demands.detail.positions.deliverDate.title" />
            <Box sx={{ width: 140 }}>
                <FormikPoolDatePicker
                    name={fieldNames.deliveryDateFrom}
                    readOnly={readonly}
                    maxDate={deliveryDateTo ? dayjs(deliveryDateTo) : undefined}
                />
            </Box>
            –
            <Box sx={{ width: 140 }}>
                <FormikPoolDatePicker
                    name={fieldNames.deliveryDateTo}
                    readOnly={readonly}
                    minDate={deliveryDateFrom ? dayjs(deliveryDateFrom) : undefined}
                />
            </Box>
            <Box component="span" sx={{ color: tokens.color.ink3 }}>
                <FormattedMessage id="app.demands.detail.positions.deliverDate.subtitle" />
            </Box>
        </Stack>
    );
};

export const PaloDemandDetailPositions = (): JSX.Element => {
    const dispatch = useThunkDispatch();
    const positionsData = useAppSelector(demandDetailPositionsDataSelector);
    // The step as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(demandDetailPositionsBaselineSelector);
    const locale = useAppSelector(bestLocaleSelector);
    const currency = useAppSelector(state => state.settings.currency);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Positions);

    const [customPosition, setCustomPosition] = useState<CustomPosition | undefined>(undefined);
    const [customData, setCustomData] = useState<CustomData | undefined>(undefined);
    const [isCustomPositionModalOpen, setIsCustomPositionModalOpen] = useState(false);
    const [showAddMultiple, setShowAddMultiple] = useState(false);

    const itemsOf = (helper: DemandDetailPositionsArrayHelpers): DemandItem[] => [
        ...(helper.form.values.demandItems as DemandItem[]),
    ];

    /**
     * Writes the new order in one go: a position is numbered by its place, and
     * renumbering after Formik's own move reads the list as it was (#2208).
     */
    const writeItems = (helper: DemandDetailPositionsArrayHelpers, items: DemandItem[]): void => {
        items.forEach((item, index) => {
            item.position = index;
        });

        helper.form.setFieldValue(fieldNames.demandItems, items);
    };

    /**
     * A row that arrives empty and waits for its article, as in the order and
     * the invoice: the article is searched for in the row itself.
     */
    const addBlankRow = (helper: DemandDetailPositionsArrayHelpers): void => {
        const items = itemsOf(helper);

        items.push(DemandItem.fromPlainObject({ name: {}, orderUnit: '', totalQuantity: 0 }));
        writeItems(helper, items);
    };

    const handleAddItem = (helper: ArrayHelpers) => (product: Product) => {
        if (product) {
            const snapshot = DemandItem.fromProduct(product, locale) as DemandItem;
            helper.push(snapshot);
        }
    };

    const handleShowAddPositionModal = (index?: number, position?: DemandItem) => {
        setIsCustomPositionModalOpen(true);
        setCustomPosition(position);
        setCustomData(
            position && index !== undefined
                ? {
                      index,
                      wasNormArticle: position.isNormArticle || false,
                  }
                : undefined
        );
    };

    const handleCustomPositionSave = (helper: ArrayHelpers) => (value: CustomPosition, data?: unknown) => {
        const savedData = data as CustomData;

        const item: DemandItem = DemandItem.fromPlainObject({
            ...value,
            currency,
            isBestPrice: false,
        });

        if (savedData?.index !== undefined) {
            if (savedData.wasNormArticle !== value.isNormArticle) {
                item.id = undefined;
            }
            helper.replace(savedData.index, item);
        } else {
            helper.push(item);
        }
        setIsCustomPositionModalOpen(false);
    };

    const handlePositionEdit = (helper: DemandDetailPositionsArrayHelpers) => (index: number) => {
        const item = positionsData.values.demandItems[index] || helper.form.values.demandItems[index];
        handleShowAddPositionModal(index, item as DemandItem);
    };

    const handleDuplicate = (helper: DemandDetailPositionsArrayHelpers) => (index: number) => {
        const item = DemandItem.fromPlainObject(helper.form.values.demandItems[index]);
        item.id = undefined;
        helper.push(item);
    };

    return (
        <FormikGroupItem
            groupItemId={DemandSteps.Positions}
            dirtyBaseline={baseline}
            initialValues={positionsData.values}
            onSubmit={data => dispatch(submitPositions(data))}
            validationSchema={validationSchema}
            initialErrors={positionsData.errors}
            initialTouched={errorsToTouch(positionsData.errors)}
            enableReinitialize={true}
            isInitialValid={positionsData.isValid}
        >
            <PaloThemeProvider>
                <Box>
                    <FieldArray name={fieldNames.demandItems}>
                        {(helper: DemandDetailPositionsArrayHelpers) => (
                            <PaloFormSection
                                title={<FormattedMessage id="app.header.positions" />}
                                action={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                                        <PaloDescriptionModeToggle />
                                        <PaloPositionsDeliveryRange readonly={readonly} />
                                    </Box>
                                }
                                collapsible={false}
                            >
                                <DemandDetailPositionsTable
                                    items={helper.form.values.demandItems}
                                    readonly={readonly}
                                    onRemove={index => {
                                        const items = itemsOf(helper);
                                        items.splice(index, 1);
                                        writeItems(helper, items);
                                    }}
                                    onDuplicate={handleDuplicate(helper)}
                                    onEdit={handlePositionEdit(helper)}
                                    onMove={(from, to) => {
                                        const items = itemsOf(helper);
                                        const [moved] = items.splice(from, 1);
                                        items.splice(to, 0, moved);
                                        writeItems(helper, items);
                                    }}
                                    onPickProduct={(index, product) => {
                                        const items = itemsOf(helper);
                                        items[index] = DemandItem.fromProduct(product, locale) as DemandItem;
                                        writeItems(helper, items);
                                    }}
                                    onDeleteMany={indices => {
                                        // From the back, so the places of the
                                        // ones still to go do not shift.
                                        const items = itemsOf(helper);
                                        [...indices]
                                            .sort((left, right) => right - left)
                                            .forEach(index => items.splice(index, 1));
                                        writeItems(helper, items);
                                    }}
                                    onDuplicateMany={indices => {
                                        const items = itemsOf(helper);
                                        [...indices]
                                            .sort((left, right) => right - left)
                                            .forEach(index => {
                                                const copy = DemandItem.fromPlainObject(items[index]);
                                                copy.id = undefined;
                                                items.splice(index + 1, 0, copy);
                                            });
                                        writeItems(helper, items);
                                    }}
                                    toolbar={
                                        !readonly && (
                                            <Stack direction="row" gap={2} alignItems="flex-end">
                                                <PoolSplitButton
                                                    variant="outlined"
                                                    color="inherit"
                                                    preSelectedIndex={0}
                                                    menuItems={[
                                                        {
                                                            label: <FormattedMessage id="app.positions.addRow" />,
                                                            displayLabel: (
                                                                <FormattedMessage id="app.positions.addRow" />
                                                            ),
                                                            icon: <AddCircleIcon />,
                                                            onClick: () => addBlankRow(helper),
                                                        },
                                                        {
                                                            label: (
                                                                <FormattedMessage id="app.freePosition.addFreePosition" />
                                                            ),
                                                            displayLabel: (
                                                                <FormattedMessage id="app.freePosition.addFreePosition" />
                                                            ),
                                                            icon: <CoinPlusIcon />,
                                                            onClick: () => handleShowAddPositionModal(),
                                                        },
                                                    ]}
                                                    sx={{ flexShrink: 0 }}
                                                />
                                                <PoolButton
                                                    variant="outlined"
                                                    color="inherit"
                                                    startIcon={<AddCircleIcon />}
                                                    onClick={() => setShowAddMultiple(true)}
                                                >
                                                    <FormattedMessage id="app.positions.addMultiple" />
                                                </PoolButton>
                                                <PaloAddPositionsDialog
                                                    open={showAddMultiple}
                                                    onClose={() => setShowAddMultiple(false)}
                                                    onSelect={product => product && handleAddItem(helper)(product)}
                                                    includeNormArticles={true}
                                                />
                                            </Stack>
                                        )
                                    }
                                />
                                <FreePositionDialog
                                    customPosition={customPosition}
                                    customData={customData}
                                    isOpen={isCustomPositionModalOpen}
                                    currencySymbol={currency}
                                    locale={locale}
                                    hideFields={[
                                        CustomPositionFields.Tax,
                                        CustomPositionFields.Price,
                                        CustomPositionFields.Quantity,
                                        CustomPositionFields.ContentQuantity,
                                        CustomPositionFields.ContentUnit,
                                    ]}
                                    requiredFields={[CustomPositionFields.OrderUnit]}
                                    onCancel={() => setIsCustomPositionModalOpen(false)}
                                    onSave={handleCustomPositionSave(helper)}
                                />
                            </PaloFormSection>
                        )}
                    </FieldArray>
                </Box>
            </PaloThemeProvider>
        </FormikGroupItem>
    );
};
